/**
 * Local GPU Pricing Utility
 *
 * Applies the LOCAL_GPU_PRICE_MULTIPLIER (from shared constants) to pricing
 * strings so the UI reflects what a desktop user running a local provider on
 * their own GPU will actually pay.
 *
 * Today the multiplier is 0.0 (100% discount -> "Free"), but if it ever
 * changes the UI will automatically show the correctly discounted value
 * instead of a hardcoded "Free" string.
 *
 * Rule of display:
 *   effective_price = base_price × LOCAL_GPU_PRICE_MULTIPLIER
 *     when running in Electron desktop AND provider is marked isLocal.
 *   Otherwise the original pricing string is returned unchanged.
 */

import type { BillingBreakdown } from '../types/videoProviders';
import sharedConstants from '../constants/shared_constants.json';
import { isDesktopApp } from './apiMode';

const LOCAL_GPU_PRICE_MULTIPLIER: number =
  sharedConstants.constants.misc.LOCAL_GPU_PRICE_MULTIPLIER ?? 0;

const LOCAL_GPU_DISCOUNT_PERCENT: number =
  sharedConstants.constants.misc.LOCAL_GPU_DISCOUNT_PERCENT ?? 100;

/** Re-export for consumers that want to display the discount itself. */
export { LOCAL_GPU_PRICE_MULTIPLIER, LOCAL_GPU_DISCOUNT_PERCENT };

/**
 * Parse every numeric token in the pricing string (including comma
 * thousand-separators), multiply by the multiplier, and rebuild the string.
 *
 * Examples (multiplier = 0):
 *   "~3 tokens/1K chars"          -> "Free"
 *   "225-600 tokens/scene"        -> "Free"
 *   "~21"                         -> "Free"
 *   "Up to 600 tokens/scene"      -> "Free"
 *   "Free"                        -> "Free"
 *
 * Examples (multiplier = 0.5):
 *   "~3 tokens/1K chars"          -> "~2 tokens/1K chars" (rounded up)
 *   "225-600 tokens/scene"        -> "113-300 tokens/scene"
 */
export function applyLocalDiscount(pricing: string, multiplier: number): string {
  if (!pricing) return pricing;
  if (multiplier === 1) return pricing;

  // Extract numeric tokens (handles "1,680" as 1680 and "~3" / "5" / "0.5")
  const numberRegex = /(\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?)/g;
  const numbers: number[] = [];
  pricing.replace(numberRegex, (match) => {
    numbers.push(parseFloat(match.replace(/,/g, '')));
    return match;
  });

  // No numeric content -> preserve original label (e.g. already "Free")
  if (numbers.length === 0) return pricing;

  // If every number collapses to 0 after the multiplier, show "Free".
  const allZero = numbers.every((n) => Math.ceil(n * multiplier) === 0);
  if (allZero) return 'Free';

  // Otherwise replace each number in place with its discounted value.
  return pricing.replace(numberRegex, (match) => {
    const value = parseFloat(match.replace(/,/g, ''));
    const discounted = Math.ceil(value * multiplier);
    return discounted.toLocaleString();
  });
}

/**
 * Return the pricing string that should be rendered for a provider.
 *
 * @param pricing    Original cloud/base pricing string (e.g. "~3 tokens/1K chars")
 * @param isLocal    True if the provider runs on the user's own hardware
 * @param opts.installed  Optional availability gate (defaults to true). Pass
 *                        false to keep showing the cloud price for local
 *                        providers that the user has not installed yet.
 */
export function getEffectivePricing(
  pricing: string | undefined,
  isLocal: boolean,
  opts: { installed?: boolean } = {}
): string {
  if (!pricing) return '';
  const { installed = true } = opts;
  if (!isDesktopApp()) return pricing;
  if (!isLocal) return pricing;
  if (!installed) return pricing;
  return applyLocalDiscount(pricing, LOCAL_GPU_PRICE_MULTIPLIER);
}

/**
 * Whether a provider is currently being billed at the local-GPU rate for the
 * running environment. Useful for swapping color / badge styling.
 */
export function isLocalPricingActive(isLocal: boolean, installed: boolean = true): boolean {
  return isDesktopApp() && isLocal && installed;
}

/**
 * Format a BillingBreakdown into a human-readable multi-line summary.
 *
 * Example output:
 *   image_generation (fooocus, local) — Free
 *   tts (kokoro_tts, local) — Free
 *   visual_media (ai_veo, cloud) — 1500 tokens
 *   ─────────────────
 *   Total — 1500 tokens
 *
 * @param breakdown  BillingBreakdown returned by the cost-estimate API.
 * @returns          Formatted multi-line string.
 */
export function formatBillingBreakdown(breakdown: BillingBreakdown): string {
  const divider = '─'.repeat(17);
  const lines: string[] = breakdown.slices.map((s) => {
    const providerPart = s.component_id ? `, ${s.component_id}` : '';
    const routePart = s.was_local ? 'local' : 'cloud';
    const costPart = s.tokens === 0 ? 'Free' : `${s.tokens} tokens`;
    return `${s.slice_id}${providerPart} (${routePart}) — ${costPart}`;
  });
  lines.push(divider);
  const totalLabel = breakdown.total_tokens === 0 ? 'Free' : `${breakdown.total_tokens} tokens`;
  lines.push(`Total — ${totalLabel}`);
  return lines.join('\n');
}
